import {resolveSketchRelationDisplayName} from '../../solver/constraint/relation-compatibility/SketchRelationDefinitions';
import type {SketchRelationKind} from '../../solver/constraint/relation-compatibility/SketchRelationKinds';
import {createSketchSolverRuntimeSnapshot} from '../../solver/constraint/solverHistory/SketchSolverSnapshotFactory';
import {createSketchSolverIslandHistoryPatch} from '../../../history/solver/SketchSolverHistoryAdapter';
import {refreshSlotOwnersDerivedVisuals} from '../../slot/refreshSlotOwnersDerivedVisuals';
import {createExistingHistorySnapshot} from '../../../history/types';
import type {SketchDimensionSolverSession} from '../application/SketchDimensionApplicationService';
import {createSketchDimensionSolverSupplementProvider} from '../createSketchDimensionSolverSupplementProvider';
import type {SketchDimensionSnapshot} from '../SketchDimensionTypes';
import {SketchParametricRuntimeApplier} from '../../solver/runtime/SketchParametricRuntimeApplier';
import {solveSketchParametricIsland} from '../../solver/runtime/solveSketchParametricIsland';
import type {SmartDimensionSketchContext} from './SketchSmartDimensionController';

/** 预演阻断原因。 */
export type SketchDimensionRehearsalBlockReason = 'no-solution' | 'over-defined';

/** 预演结果。 */
export type SketchDimensionSolveRehearsalResult =
    | {
        /** island 可解（或无 island/未支持——不驱动几何）。 */
        readonly kind: 'ready';
        /** 一次性 solver 会话；无 solver 联动时为 null。 */
        readonly session: SketchDimensionSolverSession | null;
        /**
         * 尺寸未驱动几何的原因（仅 `session === null` 且确实存在未支持项时给出）。
         *
         * 调用方据此提示用户，避免"值改了、图形不动"变成静默失败。
         */
        readonly undrivenReason?: string;
    }
    | {
        /** 当前分支/几何下无解、退化或过定义。 */
        readonly kind: 'blocked';
        /** 阻断原因（无解 / 过定义冗余）。 */
        readonly reason: SketchDimensionRehearsalBlockReason;
        /** 诊断消息。 */
        readonly message: string;
    };

/** 预演输入。 */
export interface SketchDimensionSolveRehearsalInput {
    /** 活动草图命令上下文。 */
    readonly context: SmartDimensionSketchContext;
    /** 参与求解的全部尺寸快照（创建=现有集合+预演快照；编辑=改值后的集合）。 */
    readonly dimensions: readonly SketchDimensionSnapshot[];
    /** 优先别名（持久 ID → 运行时 uuid；如用户本次点击对象），覆盖全量遍历兜底。 */
    readonly priorityAliases?: ReadonlyMap<string, string>;
    /**
     * 新建尺寸的持久 ID（创建路径专用）。
     *
     * 提供时启用过定义检测（对齐 SOLIDWORKS Make Dimension Driven?）：主解成功后
     * 再解去掉该尺寸的集合并比较 Jacobian 秩，秩不增说明新尺寸是冗余约束、会使草图
     * 过定义 → 返回 `blocked` reason=`over-defined`。修改已有尺寸不传，不触发检测。
     */
    readonly newDimensionId?: string;
    /**
     * 本次改值的尺寸 ID（编辑与 Instant2D 路径专用）。
     *
     * 线性尺寸据此生成一次性的空间稳定侧偏好；新建尺寸直接复用 `newDimensionId`，
     * 不把交互偏好写入尺寸主状态或 Undo / Redo 快照。
     */
    readonly stabilityDimensionId?: string;
    /** 渲染请求（写回后刷新）。 */
    readonly requestRender: () => void;
}

/**
 * driving 尺寸求解预演（创建确认与已有尺寸改值共享，02 计划 §7.1 尺寸驱动入口的命令侧封装）。
 *
 * 语义：
 * - island 采集失败（空草图）或含参数内核未支持项 → `ready` 且 session=null
 *   （允许提交主状态但不驱动几何，阶段 3 迁移边界：禁止部分写回）；
 * - 可解 → `ready` + 一次性会话（commit=写回运行时+输出 solver 补丁，
 *   rollback=恢复 before 几何，允许 commit 后调用）；
 * - 无解/退化 → `blocked`，由调用方决定转 driven、报错或取消。
 *
 * @param input 预演输入。
 * @returns 预演结果。
 */
export function rehearseSketchDimensionDrivingSolve(
    input: SketchDimensionSolveRehearsalInput,
): SketchDimensionSolveRehearsalResult {
    const context = input.context;
    const beforeIsland = createSketchSolverRuntimeSnapshot({
        seedObjects: [...context.collectSeedObjects()],
        supplementProvider: createSketchDimensionSolverSupplementProvider(context.repository),
    }).snapshot;

    if (!beforeIsland) {
        return {kind: 'ready', session: null};
    }

    /**
     * 身份桥接：island 成员是运行时 uuid，尺寸引用是持久 ID。
     * 先全量遍历兜底，再用调用方提供的优先别名覆盖（场景中可能存在同持久 ID
     * 的多个点对象副本，创建流以用户实际点击对象为准）。
     */
    const entityIdAliases = new Map<string, string>();
    const registerAlias = (candidate: unknown): void => {
        const persistentId = Reflect.get(candidate as object, 'persistentId');
        const runtimeId = Reflect.get(candidate as object, 'uuid');

        if (typeof persistentId === 'string' && typeof runtimeId === 'string' && persistentId !== runtimeId) {
            entityIdAliases.set(persistentId, runtimeId);
        }
    };

    context.collectSeedObjects().forEach(registerAlias);
    context.collectRuntimePoints().forEach(registerAlias);
    input.priorityAliases?.forEach((runtimeId, persistentId) => {
        entityIdAliases.set(persistentId, runtimeId);
    });

    /**
     * SOLIDWORKS 在欠定义线性尺寸改值时保持空间左侧/上侧，避免最小范数解把差值均分。
     * 该目标沿用既有 preference 层：Fix、原点重合及其它正式关系仍作为硬约束优先。
     */
    const stabilityDimensionId = input.stabilityDimensionId ?? input.newDimensionId;
    const stabilityIntent = createDimensionStablePointIntent(
        beforeIsland,
        input.dimensions.find((dimension) => dimension.dimensionId === stabilityDimensionId),
        entityIdAliases,
    );
    const operationIntents = stabilityIntent ? [stabilityIntent] : undefined;

    const solveOutcome = solveSketchParametricIsland({
        island: beforeIsland,
        dimensions: input.dimensions,
        entityIdAliases,
        operationIntents,
    });

    if (solveOutcome.applied) {
        /**
         * 过定义检测（创建路径）：主解可解时再解"去掉新尺寸"的集合，比较 Jacobian 秩。
         * 秩不增 = 新尺寸未引入独立约束 = 冗余、会使草图过定义 → 阻断并提示设为从动。
         * 秩无法比较（去新尺寸后不可解等）时不阻断，避免误伤。
         */
        if (input.newDimensionId !== undefined && solveOutcome.rank) {
            const withoutNewOutcome = solveSketchParametricIsland({
                island: beforeIsland,
                dimensions: input.dimensions.filter((dimension) => dimension.dimensionId !== input.newDimensionId),
                entityIdAliases,
                operationIntents,
            });

            if (withoutNewOutcome.rank && withoutNewOutcome.rank.rank === solveOutcome.rank.rank) {
                return {
                    kind: 'blocked',
                    reason: 'over-defined',
                    message: '该尺寸会使草图过定义（约束冗余）。设为从动（参考）尺寸即可保留而不冲突。',
                };
            }
        }

        const solvedIsland = solveOutcome.island;
        const runtimeApplier = new SketchParametricRuntimeApplier();
        const sketchId = input.dimensions[0]?.sketchId ?? context.sketchId;
        /**
         * 实体非点参数（圆/弧半径）的写回与回滚快照。
         * 点位与实体参数必须同进同退：只写点位会让"改直径不改几何"，
         * 只回滚点位会在事务失败后留下半径已变、点位复原的错位状态。
         */
        const runtimeEntities = [...context.collectSeedObjects()] as Parameters<
            typeof runtimeApplier.applyEntityParameters
        >[1];
        const beforeEntityParameters = runtimeApplier.captureEntityParameters(runtimeEntities);
        const session: SketchDimensionSolverSession = {
            commit: () => {
                /**
                 * 顺序纪律：写点（不刷边）→ 写实体参数 → 统一刷边与宿主视觉。
                 * 弧的刷新在有半径关系视图时按"定义半径"做锁定重建，
                 * 先刷边会拿旧半径把刚解好的端点吸回旧圆周（端点滞后一拍），
                 * 与端点相连的其他形状随之被带歪、运行时终态偏离 island 快照
                 * （Redo 会重放出第三种形状）。
                 */
                const applyOutcome = runtimeApplier.applyPointPositions(
                    solvedIsland,
                    context.collectRuntimePoints(),
                    {deferEdgeRefresh: true},
                );

                runtimeApplier.applyEntityParameters(solveOutcome.solvedEntityParameters, runtimeEntities);
                runtimeApplier.refreshAffectedEdges(applyOutcome.affectedEdges);
                refreshSlotOwnersDerivedVisuals(runtimeEntities);
                input.requestRender();

                return createSketchSolverIslandHistoryPatch({
                    entityId: solvedIsland.islandId,
                    operation: 'update',
                    before: createExistingHistorySnapshot(beforeIsland),
                    after: createExistingHistorySnapshot(solvedIsland),
                    metadata: {sketchId},
                });
            },
            rollback: () => {
                // 回滚同一套顺序纪律：写点（不刷边）→ 写参数 → 统一刷边与宿主视觉。
                const rollbackOutcome = runtimeApplier.applyPointPositions(
                    beforeIsland,
                    context.collectRuntimePoints(),
                    {deferEdgeRefresh: true},
                );

                runtimeApplier.applyEntityParameters(beforeEntityParameters, runtimeEntities);
                try {
                    runtimeApplier.refreshAffectedEdges(rollbackOutcome.affectedEdges);
                }
                finally {
                    // 尺寸事务与交互协调器共享同一 Worker epoch：失败事务不得延迟覆写 before。
                    runtimeApplier.invalidatePendingAsyncGeometry(rollbackOutcome.affectedEdges);
                }
                refreshSlotOwnersDerivedVisuals(runtimeEntities);
                input.requestRender();
            },
            // 求解后同岛全部尺寸的新测量值：driven 尺寸随同一笔事务原子回写显示值。
            measuredValues: solveOutcome.measuredValues,
        };

        return {kind: 'ready', session};
    }

    if (solveOutcome.solveStatus === 'no-solution' || solveOutcome.solveStatus === 'invalid-solution') {
        return {
            kind: 'blocked',
            reason: 'no-solution',
            message: solveOutcome.message ?? '尺寸在当前几何分支下无解。',
        };
    }

    /**
     * island 含参数内核暂不支持的关系/尺寸：允许提交主状态但不驱动几何。
     *
     * **必须把原因带出去**：此前这里与"空草图"返回同一个 `{session:null}`，
     * 调用方无从区分，用户看到的就是"改了尺寸值、图形一动不动且没有任何提示"，
     * 旧实现要等后续对象式入口按关系视图纠正才突然跳变；统一参数入口必须在本次写回完成。
     */
    return {
        kind: 'ready',
        session: null,
        undrivenReason: describeUndrivenReason(solveOutcome),
    };
}

/** 空间侧判定容差（草图局部坐标，米）。 */
const DIMENSION_STABLE_SIDE_TOLERANCE = 1e-9;

/**
 * 为单次线性尺寸求解创建空间稳定侧偏好。
 *
 * 水平距离保持左点，竖直距离保持上点；对齐距离优先保持左点，近竖直时保持上点。
 * 点 ID 先经持久 ID → 运行时 UUID 别名解析，因而不依赖选择顺序或直线创建方向。
 *
 * @param island 修改前的求解岛快照。
 * @param dimension 本次创建或改值的目标尺寸。
 * @param entityIdAliases 持久 ID 到运行时 UUID 的身份别名。
 * @returns 可选的单点目标偏好；尺寸不适用或引用无法解析时返回 null。
 */
function createDimensionStablePointIntent(
    island: NonNullable<ReturnType<typeof createSketchSolverRuntimeSnapshot>['snapshot']>,
    dimension: SketchDimensionSnapshot | undefined,
    entityIdAliases: ReadonlyMap<string, string>,
) {
    if (!dimension
        || dimension.role !== 'driving'
        || dimension.suppressed
        || dimension.solveState === 'dangling'
        || (dimension.dimensionKind !== 'length' && dimension.dimensionKind !== 'point-distance')) {
        return null;
    }

    const pointStateById = new Map(island.pointStates.map((pointState) => [pointState.pointId, pointState]));
    let endpointIds: readonly [string, string] | null = null;

    if (dimension.dimensionKind === 'length') {
        const reference = dimension.references[0];
        const runtimeEntityId = reference
            ? entityIdAliases.get(reference.entityId) ?? reference.entityId
            : null;
        const edgeState = runtimeEntityId
            ? island.edgeStates.find((candidate) => candidate.entityId === runtimeEntityId)
            : undefined;
        const startPointId = edgeState && typeof edgeState.geometry.startPointId === 'string'
            ? edgeState.geometry.startPointId
            : edgeState?.pointIds[0];
        const endPointId = edgeState && typeof edgeState.geometry.endPointId === 'string'
            ? edgeState.geometry.endPointId
            : edgeState?.pointIds[1];

        if (startPointId && endPointId && startPointId !== endPointId) {
            endpointIds = [startPointId, endPointId];
        }
    } else {
        const firstPointId = dimension.references[0]?.pointId;
        const secondPointId = dimension.references[1]?.pointId;
        const firstRuntimePointId = firstPointId
            ? entityIdAliases.get(firstPointId) ?? firstPointId
            : null;
        const secondRuntimePointId = secondPointId
            ? entityIdAliases.get(secondPointId) ?? secondPointId
            : null;

        if (firstRuntimePointId && secondRuntimePointId && firstRuntimePointId !== secondRuntimePointId) {
            endpointIds = [firstRuntimePointId, secondRuntimePointId];
        }
    }

    if (!endpointIds) {
        return null;
    }

    const first = pointStateById.get(endpointIds[0]);
    const second = pointStateById.get(endpointIds[1]);

    if (!first || !second) {
        return null;
    }

    const orientation = dimension.branch.orientation ?? 'aligned';
    const isVertical = orientation === 'vertical'
        || (orientation === 'aligned'
            && Math.abs(first.localPosition[0] - second.localPosition[0]) <= DIMENSION_STABLE_SIDE_TOLERANCE);
    const stablePoint = isVertical
        ? (first.localPosition[1] >= second.localPosition[1] ? first : second)
        : (first.localPosition[0] <= second.localPosition[0] ? first : second);

    return {
        kind: 'point-target' as const,
        intentId: `dimension:${dimension.dimensionId}:stable-side`,
        isDirectManipulation: false,
        pointId: stablePoint.pointId,
        localPosition: stablePoint.localPosition,
    };
}

/**
 * 组织面向用户的"尺寸未驱动几何"说明。
 *
 * 求解器的 message 面向诊断（带约束 ID），这里按关系显示名去重成一句人话，
 * 让用户知道"值已改、图形没动"的确切原因与涉及的关系类型。
 *
 * @param outcome 求解结果。
 * @returns 用户可读的说明。
 */
function describeUndrivenReason(outcome: ReturnType<typeof solveSketchParametricIsland>): string {
    const relationNames = [...new Set(
        (outcome.build?.unsupportedConstraints ?? [])
            .map((item) => resolveSketchRelationDisplayName(item.kind as SketchRelationKind)),
    )];
    const dimensionKinds = [...new Set(
        (outcome.build?.unsupportedDimensions ?? []).map((item) => item.kind),
    )];
    const parts: string[] = [];

    if (relationNames.length > 0) {
        parts.push(`几何关系「${relationNames.join('、')}」`);
    }
    if (dimensionKinds.length > 0) {
        parts.push(`尺寸类型「${dimensionKinds.join('、')}」`);
    }

    return parts.length > 0
        ? `尺寸值已更新，但草图中的${parts.join('与')}暂不支持参数驱动，几何未随之更新。`
        : '尺寸值已更新，但当前草图暂不支持参数驱动，几何未随之更新。';
}
